import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowUpShapeFill, SnsInsta, SnsX, Xmark } from '@peacecover/icon';
import { getFanStyleTagBackgroundOpacityClassName } from '../utils/getFanStyleTagBgColor';
import ProfileCardTag from './ProfileCardTag';
import type { components } from '@pocapan/shared/types/schema';
import { getRandomBackgroundColor } from '../utils/getRandomBackgroundColor';

type BuddyProfileCard = components['schemas']['ApiV2BuddyProfileCard'];

interface FanBuddyProfileCardProps {
  size?: 'tiny' | 'small' | 'large' | 'extraLarge';
  buddyProfileCard: BuddyProfileCard;
  isMine?: boolean;
  isHiddenTag?: boolean;
  onCardClick?: () => void;
  onRequestOpenCard?: () => void;
  redirectPath?: string;
  styleAnchorRef?: RefObject<HTMLDivElement>;
  className?: string;
}

const sizeConfig = {
  tiny: {
    container:
      'w-[223px] min-h-[344px] p-[16px] rounded-[10px] border-[0.7px] shadow-[0_0.741px_3.704px_0_rgba(0,0,0,0.07)]',
    header: 'mt-[34px] px-[19px] mb-[27px]',
    ageGroup: 'text-[8px] leading-[10px] mb-[6px] h-[10px]',
    nickname: 'text-[23px] leading-[28px]',
    fandoms: 'text-[14px] leading-[17px] mt-[1.5px]',
    snsText: 'text-[7px] leading-[8px] mr-[8px]',
    subTitle: 'text-[7px] leading-[8px]',
    gap: 'gap-[18px]',
    bottomPadding: 'pt-[28px]',
    tagGap: 'gap-[11px]',
    imageSize: 'w-[91px] h-[91px]',
    imageWrapper: 'w-[91px] h-[66px]',
    imagePosition: 'top-[-19px] right-[-25px]',
    snsBox: 'w-[20px] h-[20px]',
    iconSize: 17.04,
    minSize: 'min-h-[87px]',
    emptyTagSize: '!text-[19px]'
  },
  small: {
    container:
      'w-[251px] min-h-[387px] py-[19px] pl-[19px] pr-[6px] rounded-[12px] border-[0.83px] shadow-[0_0.833px_4.167px_0_rgba(0,0,0,0.07)]',
    header: 'mt-[37px] px-[21px] mb-[32px]',
    ageGroup: 'text-[9px] leading-[11px] mb-[7px] h-[11px]',
    nickname: 'text-[25px] leading-[30px]',
    fandoms: 'text-[16px] leading-[19px] mt-[1.7px] ml-[1px]',
    snsText: 'text-[8px] leading-[10px] mr-[10px]',
    subTitle: 'text-[8px] leading-[10px]',
    gap: 'gap-[20px]',
    bottomPadding: 'pt-[32px]',
    tagGap: 'gap-[12px]',
    imageSize: 'w-[103px] h-[103px]',
    imageWrapper: 'w-[103px] h-[75px]',
    imagePosition: 'top-[-22px] right-[-28px]',
    snsBox: 'w-[23px] h-[23px]',
    iconSize: 19.167,
    minSize: 'min-h-[103px]',
    emptyTagSize: '!text-[19px]'
  },
  large: {
    container:
      'w-[300px] min-h-[462px] p-[22px] rounded-[14px] border-[1px] shadow-[0_0.996px_4.98px_0_rgba(0,0,0,0.07)]',
    header: 'mt-[45px] px-[26px] mb-[38px]',
    ageGroup: 'text-[11px] leading-[13px] mb-[8px] h-[13px]',
    nickname: 'text-[30px] leading-[36px]',
    fandoms: 'text-[19px] leading-[23px] mt-[2px]',
    snsText: 'text-[8px] leading-[10px] mr-[10px]',
    subTitle: 'text-[10px] leading-[12px]',
    gap: 'gap-[24px]',
    bottomPadding: 'pt-[38px]',
    tagGap: 'gap-[14px]',
    imageSize: 'w-[123px] h-[123px]',
    imageWrapper: 'w-[123px] h-[89px]',
    imagePosition: 'top-[-26px] right-[-33px]',
    snsBox: 'w-[27px] h-[27px]',
    iconSize: 22.91,
    minSize: 'min-h-[135px]',
    emptyTagSize: '!text-[19px]'
  },
  extraLarge: {
    container:
      'w-[335px] min-h-[517px] p-[25px] rounded-[16px] border-[1px] shadow-[0_0.996px_4.98px_0_rgba(0,0,0,0.07)]',
    header: 'mt-[50px] px-[29px] mb-[42px]',
    ageGroup: 'text-[12px] leading-[14px] mb-[9px] h-[14px]',
    nickname: 'text-[33px] leading-[39px]',
    fandoms: 'text-[21px] leading-[25px] mt-[2px]',
    snsText: 'text-[9px] leading-[11px] mr-[11px]',
    subTitle: 'text-[11px] leading-[13px]',
    gap: 'gap-[26.8px]',
    bottomPadding: 'pt-[42px]',
    tagGap: 'gap-[16px]',
    imageSize: 'w-[137px] h-[137px]',
    imageWrapper: 'w-[137px] h-[100px]',
    imagePosition: 'top-[-30px] right-[-37px]',
    snsBox: 'w-[30px] h-[30px]',
    iconSize: 26,
    minSize: 'min-h-[135px]',
    emptyTagSize: '!text-[19px]'
  }
};

const EMPTY_PROFILE_KEYWORDS_OTHER = [
  '전화싫어',
  '지독한 얼빠',
  '알콜 쓰레기',
  '연락중독',
  '여행매니아'
];
const EMPTY_PROFILE_KEYWORDS_MINE = ['여행매니아', '전화싫어', '밈천재', '지독한 얼빠'];

// @TODO: phase2에서 레벨 별로 hidden 처리 필요
const FanBuddyProfileCard = ({
  size = 'small',
  buddyProfileCard,
  isMine = true,
  isHiddenTag = false,
  onCardClick,
  onRequestOpenCard,
  redirectPath,
  styleAnchorRef,
  className = ''
}: FanBuddyProfileCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isMoreKeywordsOpen, setIsMoreKeywordsOpen] = useState(false);
  const [isMoreFandomsOpen, setIsMoreFandomsOpen] = useState(false);
  const [revealedSns, setRevealedSns] = useState<null | 'instagram' | 'twitter'>(null);

  const hasBuddyProfileCard = buddyProfileCard?.cardStatus === 'v2';
  const hasBuddyKeywords = Boolean(buddyProfileCard?.keywords?.length);
  const fanStyleTags = hasBuddyProfileCard
    ? [
        t(`FanStyleOptions.${buddyProfileCard?.fanCareerLevel}.label`),
        t(`FanStyleOptions.${buddyProfileCard?.stanningStyle}.label`),
        t(`FanStyleOptions.${buddyProfileCard?.affectionType}.label`),
        t(`FanStyleOptions.${buddyProfileCard?.fantasyType}.label`),
        t(`FanStyleOptions.${buddyProfileCard?.fandomSocialStyle}.label`)
      ]
    : [];
  const totalKeywords = [
    ...fanStyleTags,
    ...(buddyProfileCard?.keywords?.map((keyword) => keyword.name) ?? [])
  ];
  const [fallbackBackgroundColor, setFallbackBackgroundColor] = useState(() =>
    getRandomBackgroundColor()
  );
  const userProfileId = buddyProfileCard?.userProfileId;
  useEffect(() => {
    if (!userProfileId) return;
    setFallbackBackgroundColor(getRandomBackgroundColor());
  }, [userProfileId]);
  const backgroundColor = buddyProfileCard?.backgroundColor ?? fallbackBackgroundColor;
  const emptyProfileKeywords = isMine ? EMPTY_PROFILE_KEYWORDS_MINE : EMPTY_PROFILE_KEYWORDS_OTHER;
  const isFollowBack = buddyProfileCard?.isMutualFollow ?? false;
  const hasInstagramId = Boolean(buddyProfileCard?.instagramId);
  const hasTwitterId = Boolean(buddyProfileCard?.twitterId);
  const hasAnySnsId = hasInstagramId || hasTwitterId;

  const revealedSnsText = (() => {
    if (!hasAnySnsId) return;
    if (!isFollowBack) return t('FanBuddyProfileCard.onlyMutualFollow');
    if (!revealedSns) return t('FanBuddyProfileCard.clickAndShow');
    if (revealedSns === 'instagram' && buddyProfileCard?.instagramId) {
      return `insta @${buddyProfileCard.instagramId}`;
    }
    if (revealedSns === 'twitter' && buddyProfileCard?.twitterId) {
      return `twitter @${buddyProfileCard.twitterId}`;
    }
    return t('FanBuddyProfileCard.clickAndShow');
  })();

  const handleCreateProfile = () => {
    if (redirectPath) {
      navigate('/fan-buddy/intro', { state: { redirectPath }, replace: true });
    } else {
      navigate('/fan-buddy/intro');
    }
  };

  const openMoreKeywords = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRequestOpenCard?.();
    setIsMoreKeywordsOpen(true);
  };

  const openMoreFandoms = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRequestOpenCard?.();
    setIsMoreFandomsOpen(true);
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: 내부에 <button>들이 있어 최상위 컨테이너를 <button>으로 바꾸면 중첩 버튼이 되어 HTML/a11y가 더 악화됩니다.
    <div
      role="button"
      tabIndex={0}
      onClick={onCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCardClick?.();
        }
      }}
      className={`flex flex-col overflow-hidden relative ${sizeConfig[size].container} ${className}`}
      style={{ backgroundColor }}
    >
      {/* --- 상단 영역 --- */}
      <div className={`${sizeConfig[size].header}`}>
        <div className="z-10 text-left">
          {buddyProfileCard?.isTheSameAge ? (
            <p className={`font-bold text-[#444444] ${sizeConfig[size].ageGroup}`}>
              {t('FanBuddyProfileCard.sameAge')}
            </p>
          ) : (
            <div className={`${sizeConfig[size].ageGroup}`} />
          )}
          <div className="relative z-10">
            <div className="relative">
              <p
                className={`font-bold text-[#222222] max-w-[180px] truncate ${sizeConfig[size].nickname}`}
              >
                {buddyProfileCard?.nickname}
              </p>
            </div>
            <p className={`font-bold text-[#444444] ${sizeConfig[size].fandoms}`}>
              {buddyProfileCard?.likedArtists
                ?.slice(0, 3)
                .map((artist) => artist.fandomName)
                .join(' · ')}
              {buddyProfileCard?.likedArtists && buddyProfileCard?.likedArtists?.length > 3 && (
                <button type="button" className="ml-[1px]" onClick={openMoreFandoms}>
                  +{buddyProfileCard?.likedArtists?.length - 3}
                </button>
              )}
            </p>
            {isMoreFandomsOpen && (
              <MoreFandomsSection
                onClose={() => {
                  setIsMoreFandomsOpen(false);
                }}
                fandoms={buddyProfileCard?.likedArtists?.map((artist) => artist.fandomName ?? '')}
                size={size}
              />
            )}
          </div>
        </div>
        {/* 우측 상단 프로필 이미지 (사진처럼 위쪽에 배치) */}
        {buddyProfileCard?.thumbnailUrl && (
          <div className={`absolute ${sizeConfig[size].imagePosition}`}>
            <div
              className={`relative ${sizeConfig[size].imageSize} overflow-hidden rotate-[-10.7deg] isolate transform-gpu`}
              style={{ backgroundColor }}
            >
              <img
                src={buddyProfileCard?.thumbnailUrl}
                alt="profile"
                className="absolute inset-0 z-0 w-full h-full object-cover mix-blend-hard-light scale-[1.06]"
                style={{
                  filter: 'grayscale(1) blur(2.39px)'
                }}
              />
              <img
                src={buddyProfileCard?.thumbnailUrl}
                alt="profile"
                className="w-full h-full object-cover mix-blend-hard-light absolute top-0 left-0 z-[1]"
              />
              <div
                className={`${sizeConfig[size].imageWrapper} absolute bottom-0 left-0 z-[2]`}
                style={{
                  background: `linear-gradient(180deg, rgba(255, 0, 85, 0.00) 0%, ${backgroundColor} 81.73%, ${backgroundColor} 100%)`
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* --- 중앙 콘텐츠 영역 --- */}
      <div className={`flex flex-col relative ${sizeConfig[size].gap}`}>
        {isMoreKeywordsOpen && (
          <MoreKeywordsSection
            onClose={() => {
              setIsMoreKeywordsOpen(false);
            }}
            favoriteMembers={buddyProfileCard?.likedMembers?.map((member) => member.name)}
            totalKeywords={totalKeywords}
            size={size}
          />
        )}
        {/* 최애 섹션 */}
        <div className={`flex items-start ${sizeConfig[size].tagGap}`}>
          <div className="w-[12px] flex justify-center self-start">
            <p
              className={`font-bold text-[#444444] [writing-mode:sideways-lr] whitespace-nowrap ${sizeConfig[size].subTitle}`}
            >
              {t('FanBuddyProfileCard.favoriteMembers')}
            </p>
          </div>
          <div className="flex flex-wrap gap-[1px] gap-y-[1px] flex-1">
            {buddyProfileCard?.likedMembers?.slice(0, 3).map((member) => (
              <ProfileCardTag key={member.name} tag={member.name} size={size} />
            ))}
            {buddyProfileCard?.likedMembers?.length > 3 && (
              <button
                type="button"
                onClick={openMoreKeywords}
                className="p-0 m-0 border-0 bg-transparent leading-none inline-flex items-center"
              >
                <ProfileCardTag
                  tag={`+${buddyProfileCard?.likedMembers?.length - 3}`}
                  size={size}
                />
              </button>
            )}
          </div>
        </div>

        {/* 덕질 스타일 섹션 (외부에서 덮어씌울 위치를 잡기 위한 앵커 ref) */}
        <div
          className={`flex items-start ${sizeConfig[size].tagGap} ${sizeConfig[size].minSize} `}
          ref={styleAnchorRef}
        >
          <div className="w-[12px] flex justify-center self-start">
            <p
              className={`font-bold text-[#444444] [writing-mode:sideways-lr] whitespace-nowrap ${sizeConfig[size].subTitle}`}
            >
              {t('FanBuddyProfileCard.fanStyle')}
            </p>
          </div>

          <div className="flex flex-wrap gap-[1px] flex-1">
            {fanStyleTags?.map((tag) => (
              <ProfileCardTag
                key={tag}
                tag={tag}
                size={size}
                backgroundOpacityClassName={getFanStyleTagBackgroundOpacityClassName(tag)}
              />
            ))}
            {isMine && !hasBuddyProfileCard && (
              <button
                type="button"
                onClick={handleCreateProfile}
                className="flex items-center gap-[1px] pl-[2px] pr-[1px] rounded-[1px] bg-[rgba(34,34,34,0.10)] mb-[1px]"
              >
                <p className="text-[21px] font-bold text-pure-white leading-[25px]">
                  {t('FanBuddyProfileCard.createProfile')}
                </p>
                <ArrowUpShapeFill size={24} className="text-pure-white" />
              </button>
            )}

            {(hasBuddyKeywords
              ? buddyProfileCard?.keywords?.slice(0, 3).map((keyword) => keyword.name)
              : emptyProfileKeywords
            )?.map((keyword) => (
              <ProfileCardTag
                key={keyword}
                tag={keyword}
                size={size}
                isHidden={!hasBuddyKeywords}
              />
            ))}

            {hasBuddyKeywords && buddyProfileCard?.keywords?.length > 3 && (
              <button
                type="button"
                onClick={openMoreKeywords}
                className="p-0 m-0 border-0 bg-transparent leading-none inline-flex items-center"
              >
                <ProfileCardTag tag={`+${buddyProfileCard?.keywords?.length - 3}`} size={size} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* --- 하단 영역 --- */}
      <div className={`${sizeConfig[size].bottomPadding}`}>
        <div className={`flex items-start ${sizeConfig[size].tagGap}`}>
          <div className="w-[12px] flex justify-center self-start">
            <p
              className={`font-bold text-[#444444] [writing-mode:sideways-lr] whitespace-nowrap ${sizeConfig[size].subTitle}`}
            >
              SNS
            </p>
          </div>
          <div className="flex gap-[2px] items-center flex-1">
            {hasInstagramId && (
              <button
                type="button"
                disabled={!isFollowBack}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isFollowBack) return;
                  setRevealedSns((prev) => (prev === 'instagram' ? null : 'instagram'));
                }}
                className={`bg-[#222222] ${sizeConfig[size].snsBox} flex items-center justify-center p-0 leading-none rounded-[1px]`}
              >
                <SnsInsta
                  size={sizeConfig[size].iconSize}
                  className="text-pure-white block m-auto"
                />
              </button>
            )}
            {hasTwitterId && (
              <button
                type="button"
                disabled={!isFollowBack}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isFollowBack) return;
                  setRevealedSns((prev) => (prev === 'twitter' ? null : 'twitter'));
                }}
                className={`bg-[#222222] ${sizeConfig[size].snsBox} flex items-center justify-center p-0 leading-none rounded-[1px]`}
              >
                <SnsX size={sizeConfig[size].iconSize} className="text-pure-white block m-auto" />
              </button>
            )}
            {!hasAnySnsId && (
              <ProfileCardTag
                tag={t('FanBuddyProfileCard.unregistered')}
                size={size}
                className="!text-[19px]"
              />
            )}
            {(buddyProfileCard?.instagramId || buddyProfileCard?.twitterId || !isFollowBack) && (
              <p className={`text-[#222222] font-semibold ml-auto ${sizeConfig[size].snsText}`}>
                {revealedSnsText}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FanBuddyProfileCard;

const MoreFandomsPositionClassName = {
  tiny: 'right-[-19px] bottom-[-8px]',
  small: 'right-[-21px] bottom-[-10px]',
  large: 'right-[-26px] bottom-[-12px]',
  extraLarge: 'right-[-28px] bottom-[-14px]'
} as const;

const MoreFandomsSection = ({
  onClose,
  fandoms,
  size
}: {
  onClose: () => void;
  fandoms: string[];
  size: 'tiny' | 'small' | 'large' | 'extraLarge';
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={`absolute top-0 ${MoreFandomsPositionClassName[size]} left-0 py-[10px] px-[9px] bg-black text-pure-white text-left z-[15] overflow-y-scroll flex flex-col gap-[15px]`}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-[10px] right-[7px]"
      >
        <Xmark size={12} />
      </button>
      <div>
        <p className="text-[11px] font-bold">{t('FanBuddyProfileCard.activeFandoms')}</p>
        <p className="text-[13px]">{fandoms?.join(', ')}</p>
      </div>
    </div>
  );
};

const MoreKeywordsPositionClassName = {
  tiny: 'left-[18px]',
  small: 'left-[22px]',
  large: 'left-[25px]',
  extraLarge: 'left-[28px]'
} as const;

const MoreKeywordsSection = ({
  onClose,
  favoriteMembers,
  totalKeywords,
  size
}: {
  onClose: () => void;
  favoriteMembers: string[];
  totalKeywords: string[];
  size: 'tiny' | 'small' | 'large' | 'extraLarge';
}) => {
  const { t } = useTranslation();
  return (
    <div
      className={`absolute top-0 bottom-0 right-0 ${MoreKeywordsPositionClassName[size]} py-[10px] px-[9px] bg-black text-pure-white text-left z-[15] overflow-y-scroll flex flex-col gap-[15px]`}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-[10px] right-[7px]"
      >
        <Xmark size={12} />
      </button>
      <div>
        <p className="text-[11px] font-bold">{t('FanBuddyProfileCard.favoriteMembers')}</p>
        <p className="text-[13px]">{favoriteMembers?.join(', ')}</p>
      </div>
      <div>
        <p className="text-[11px] font-bold">{t('FanBuddyProfileCard.keywords')}</p>
        <p className="text-[13px]">{totalKeywords?.join(', ')}</p>
      </div>
    </div>
  );
};
